import { useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Get display name for node type
 * @param {string} nodeType - Normalized node type
 * @returns {string} Display name
 */
const getNodeTypeDisplayName = (nodeType) => {
  const typeMap = {
    attachment: "Attachment",
    canva: "Canvas",
    "rich-text": "Rich Text",
    video: "Video",
    form: "Form",
  };
  return typeMap[nodeType] || nodeType;
};

/**
 * Utility function to generate node indexing names from nodes data
 * Can be called from anywhere (not just React components)
 * 
 * @param {Array} nodesData - Array of nodes data
 * @returns {Object} Mapping of tree_node_id to generated indexing name
 */
export const generateNodeIndexing = (nodesData) => {
  if (!Array.isArray(nodesData) || nodesData.length === 0) {
    return {};
  }

  const map = {};
  let globalStepCounter = 0;
  const childNodeCounters = {
    attachment: 0,
    canva: 0,
    "rich-text": 0,
    video: 0,
    form: 0,
  };

  const CHILD_NODE_ID_FIELDS = [
    "attachment_tree_node_id",
    "video_tree_node_id",
    "rich_text_node_id",
    "canvas_tree_node_id",
    "form_tree_node_id",
  ];

  const getSiblingOrderValue = (node) => {
    if (!node) return 0;
    const order =
      node.sibling_order ??
      node.procedure_tree?.sibling_order ??
      node.detail?.procedure_tree?.sibling_order ??
      '0';
    const parsed = parseInt(order, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const sortBySiblingOrder = (items = []) =>
    [...items].sort((a, b) => getSiblingOrderValue(a) - getSiblingOrderValue(b));

  const addMapping = (mapObj, id, label) => {
    if (!id) return;
    mapObj[id] = label;
  };

  const assignSectionLabel = (section, label) => {
    if (!section) return;
    addMapping(map, section.tree_node_id, label);
    addMapping(map, section.section_id_ref, label);
  };

  const assignStepLabel = (step, label) => {
    if (!step) return;
    addMapping(map, step.tree_node_id, label);
    addMapping(map, step.step_id_ref, label);
  };

  const assignSubstepLabel = (substep, label) => {
    if (!substep) return;
    addMapping(map, substep.tree_node_id, label);
    addMapping(map, substep.substep_id_ref, label);
  };

  const assignChildLabel = (child, label) => {
    if (!child) return;
    addMapping(map, child.tree_node_id, label);
    const detail = child.detail || {};
    CHILD_NODE_ID_FIELDS.forEach((field) => {
      if (detail[field]) {
        addMapping(map, detail[field], label);
      }
    });
  };

  // Helper function to get normalized type
  const normalizeType = (type) => {
    if (!type) return null;
    const lowerType = type.toLowerCase();
    if (lowerType === "richtext" || lowerType === "rich-text") {
      return "rich-text";
    }
    return lowerType;
  };

  // Helper function to generate alphanumeric suffix (A, B, C, ...)
  const generateAlphanumericSuffix = (index) => {
    return String.fromCharCode(65 + index); // 65 is 'A' in ASCII
  };

  // Sort sections by sibling_order
  const sortedSections = [...nodesData].sort((a, b) => {
    const orderA = parseInt(a.sibling_order || "0", 10);
    const orderB = parseInt(b.sibling_order || "0", 10);
    return orderA - orderB;
  });

  // Process each section
  sortedSections.forEach((section, sectionIndex) => {
    if (section.type !== "section" || section.is_deleted) {
      return;
    }

    // Generate section name
    const sectionNumber = sectionIndex + 1;
    const sectionLabel = `Section ${sectionNumber}`;
    assignSectionLabel(section, sectionLabel);

    // Process steps within section
    if (section.steps && Array.isArray(section.steps)) {
      // Sort steps by sibling_order
      const sortedSteps = sortBySiblingOrder(section.steps);

      sortedSteps.forEach((step) => {
        if (step.type !== "step" || step.is_deleted) {
          return;
        }

        globalStepCounter++;
        const stepNumber = globalStepCounter;
        const stepLabel = `Step ${stepNumber}`;
        assignStepLabel(step, stepLabel);

        // Process children within step (direct children, not in substeps)
        if (step.children && Array.isArray(step.children)) {
          const sortedStepChildren = sortBySiblingOrder(step.children);
          sortedStepChildren.forEach((child) => {
            if (child.is_deleted) return;
            
            const childType = normalizeType(child.type);
            if (childNodeCounters.hasOwnProperty(childType)) {
              childNodeCounters[childType]++;
              const nodeTypeName = getNodeTypeDisplayName(childType);
              const childLabel = `${nodeTypeName} ${childNodeCounters[childType]}`;
              assignChildLabel(child, childLabel);
            }
          });
        }

        // Process substeps within step
        if (step.substeps && Array.isArray(step.substeps)) {
          // Sort substeps by sibling_order
          const sortedSubsteps = sortBySiblingOrder(step.substeps);

          sortedSubsteps.forEach((substep, substepIndex) => {
            if (substep.type !== "substep" || substep.is_deleted) {
              return;
            }

            const suffix = generateAlphanumericSuffix(substepIndex);
            const substepLabel = `Substep ${stepNumber}${suffix}`;
            assignSubstepLabel(substep, substepLabel);

            // Process children within substep
            if (substep.children && Array.isArray(substep.children)) {
              const sortedSubstepChildren = sortBySiblingOrder(substep.children);
              sortedSubstepChildren.forEach((child) => {
                if (child.is_deleted) return;
                
                const childType = normalizeType(child.type);
                if (childNodeCounters.hasOwnProperty(childType)) {
                  childNodeCounters[childType]++;
                  const nodeTypeName = getNodeTypeDisplayName(childType);
                  const childLabel = `${nodeTypeName} ${childNodeCounters[childType]}`;
                  assignChildLabel(child, childLabel);
                }
              });
            }
          });
        }
      });
    }

    // Process children directly under section (if any)
    if (section.children && Array.isArray(section.children)) {
      const sortedSectionChildren = sortBySiblingOrder(section.children);
      sortedSectionChildren.forEach((child) => {
        if (child.is_deleted) return;
        
        const childType = normalizeType(child.type);
        if (childNodeCounters.hasOwnProperty(childType)) {
          childNodeCounters[childType]++;
          const nodeTypeName = getNodeTypeDisplayName(childType);
          const childLabel = `${nodeTypeName} ${childNodeCounters[childType]}`;
          assignChildLabel(child, childLabel);
        }
      });
    }
  });

  return map;
};

/**
 * Hook to generate node indexing names for sections, steps, substeps, and child nodes
 * 
 * @param {Array} nodesData - Optional. Array of nodes data. If not provided, will fetch from Redux slice
 * @returns {Object} Mapping of tree_node_id to generated indexing name
 * 
 * @example
 * {
 *   "treenodeidofsection": "Section 1",
 *   "sectiononesteponetreenodeid": "Step 1",
 *   "sectiononeteptwotreenodeid": "Step 2",
 *   "treenodeidofsection2": "Section 2",
 *   "sectiontwosteponetreenodeid": "Step 3",
 *   "sectiontwosteptwotreenodeid": "Step 4",
 *   "substep1atreenodeid": "Substep 1A",
 *   "substep1btreenodeid": "Substep 1B",
 *   "attachment1treenodeid": "Attachment 1",
 *   "canvas1treenodeid": "Canvas 1"
 * }
 */
const useNodeIndexing = (nodesData = null) => {
  // Get nodes from Redux if not provided
  const allRevisionNodes = useSelector(
    (state) => state.procedureRevision.allRevisionNodes
  );

  const indexingMap = useMemo(() => {
    // Use provided data or fall back to Redux
    const data = nodesData || allRevisionNodes || [];
    return generateNodeIndexing(data);
  }, [nodesData, allRevisionNodes]);

  return indexingMap;
};

export default useNodeIndexing;

